
import { Sale } from "@/models/base";
import { ContactPeppolEndpoint } from "@/models/base/contact-peppol-endpoint";
import { BillitService } from "@/plugins/billit/services/billit-service";
import { IParticipantInfoResponse } from "@/plugins/billit/utils/participant-info";
import { HttpError } from "@/utils/services/http-error";
import Vue from "vue";
import { ISelectItem } from "winfakt-vue-components";
import AppMissingModule from "@/components/common/MissingModule.vue";
import { AuthService } from "@/services/auth-service";
import { MODULE } from "@/config/modules";
import AppFileUpload from "@/components/FileUpload.vue";
import AppErrorMessageBox from "@/components/common/ErrorMessageBox.vue";
import { DossierConfigService } from "@/services";

export default Vue.extend({
	data() {
		return {
			checkResponse: null as null | IParticipantInfoResponse,
			po: "",
			department: "",
			documentType: "Invoice",
			sending: false,
			files: [] as File[],
			settings: DossierConfigService.getPluginConfig().UBL,
			selectedEndpointID: ""
		};
	},
	props: {
		sale: {
			type: Object as() => Sale,
			required: true
		}
	},
	computed: {
		documentTypes():ISelectItem<string>[] {
			let defaultDocs = [{
				text: "Invoice",
				value: "Invoice"
			}];
			if (!this.checkResponse || !this.checkResponse.DocumentTypes){
				return defaultDocs;
			}
			let extraDocs = this.checkResponse.DocumentTypes.map(d => {
				return {
					text: d,
					value: d
				} as ISelectItem<string>;
			});
			let result = [...defaultDocs, ...extraDocs];

			return result.filter((f,i)=>result.findIndex(a=>a.value == f.value) == i);
		},
		valid():boolean {
			if (!this.checkResponse) {
				return false;
			}
			if (!this.checkResponse.Registered) {
				return false;
			}
			return true;
		},
		moduleId():number{
			return MODULE.ELECTRONISCHE_FACTURATIE;
		},
		hasModule():boolean{
			let mod = AuthService.wfCompany.Modules.find(m=>m.ID == this.moduleId);
			if (!mod) {
				return false;
			}
			if (!mod.MetaData) return false;
			let sentsCount = mod.MetaData["max-peppol-sents-per-month"];
			if (!sentsCount) return false;
			if (sentsCount == "0") return false;
			return true;
		},
		maxFileSize():number {
			let baseSize = 50e6;
			for (let file of this.files) {
				baseSize -= file.size;
			}
			return baseSize;
		},
		isDossierFilledIn():boolean{
			return AuthService.wfDossier.isFilledIn();
		},
		isDefaultBankFilledIn():boolean {
			return this.settings.isPeppolConfigured();
		},
		endpointItems():ISelectItem<string>[] {
			if (!this.sale.Contact) return [];
			let items = this.sale.Contact.PeppolEndpoints
				.filter((e:ContactPeppolEndpoint) => e.getFullID() !== "")
				.map((e:ContactPeppolEndpoint) => ({ value: e.getFullID(), text: e.getDisplayName() } as ISelectItem<string>));
			return items.filter((item:ISelectItem<string>, i:number) => items.findIndex((x:ISelectItem<string>) => x.value === item.value) === i);
		}
	},
	watch: {
		"sale.Contact": {
			immediate: true,
			handler() {
				if (!this.sale.Contact) return;
				let endpoints = this.sale.Contact.PeppolEndpoints.filter((e:ContactPeppolEndpoint) => e.getFullID() !== "");
				let preferred = endpoints.find((e:ContactPeppolEndpoint) => e.IsDefault) || endpoints[0];
				if (preferred) {
					this.selectedEndpointID = preferred.getFullID();
				} else {
					this.selectedEndpointID = "";
				}
			}
		},
		selectedEndpointID() {
			this.checkResponse = null;
			this.refreshParticipantInfo();
		}
	},
	methods: {
		close() {
			this.$emit("close", false);
		},
		async send() {
			if (this.sale.LastSentViaPeppolDate) {
				let date = this.sale.LastSentViaPeppolDate.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
				let confirm = await this.$confirmBuilder().setWarningTitle().setMessage(this.$ct("confirm-resend-via-peppol", {date})).show();
				if (!confirm) return;
			}
			this.sending = true;
			try{
				await BillitService.sendSale(this.sale.ID, this.documentType, this.po, this.department, this.files, this.selectedEndpointID);
				this.$wf.notify(this.$ct("peppol-document-sent"), "success", 3000, "check");
				this.close();
			}catch(err){
				this.sending = false;
				let hErr = err as HttpError;
				hErr.dontShow = true;
				this.$wf.notify(hErr.getText(), "danger", 3000, "exclamation-triangle");
			}
		},
		onFileInput(file:File){
			this.files.push(file);
		},
		removeFile(i:number){
			this.files.splice(i,1);
		},
		editDossierSettings(){
			this.$router.push(`/settings/company/dossier/${AuthService.wfDossier.id}`);
		},
		editBanks(){
			this.$router.push("/settings/ubl?tab=peppol");
		},
		async refreshParticipantInfo() {
			let lookup = this.selectedEndpointID;
			if (!lookup && this.sale.Contact) {
				lookup = this.sale.Contact.getSearchPeppolEndpointString();
			}
			if (!lookup) {
				this.checkResponse = {
					Registered: false,
					DocumentTypes: ["Invoice"]
				};
				return;
			}
			try {
				this.checkResponse = await BillitService.getParticipantInfo(lookup);
			} catch (err) {
				this.checkResponse = {
					Registered: false,
					DocumentTypes: ["Invoice"]
				};
			}
		}
	},
	async mounted() {
		await this.refreshParticipantInfo();
	},
	components: {
		AppMissingModule,
		AppFileUpload,
		AppErrorMessageBox
	},
	i18nEx: {
		componentPrefix: "components.sales"
	}
});
